import React, { useCallback, useEffect, useState } from "react";
import {
  Col,
  Row,
  Form,
  Flex,
  Image,
  Table,
  Select,
  Button,
  Divider,
  GetProps,
  DatePicker,
  Typography,
  ConfigProvider,
  type FormProps,
  type TableColumnsType,
} from "antd";
import "dayjs/locale/vi";
import dayjs from "dayjs";
import "./Report.scss";
import { formatPrice, handleGetUrlGuide } from "../../utils/helpers";
import IconFileExport from "../../components/Icons/IconFileExport";
import IconExportExcel from "../../components/Icons/IconExportExcel";
import IconExclamationCircle from "../../assets/images/ExclamationCircle.svg";
import { DataReport, OfficeData, ValueOptions } from "../../types/report";
import { DEFAULT_PAGE_SIZE } from "antd/es/table/hooks/usePagination";
import { Constant } from "../../config/constant";
import { toast } from "react-toastify";
import { getListAgentAPI } from "../../api/report";
import { optionsBookingDate } from "./ReportDetailCompany";
import { useAppDispatch, useAppSelector } from "../../redux/hooks";
import {
  getListReportTotalAgent,
  setFilterAgent,
  setFilterRangeDate,
  setFilterBookingDate,
  getListReportTotalAgentExcel,
  setPerPage
} from "../../redux/slice/report/reportTotalAgentSlice";
import { IExcelColumn } from "antd-table-saveas-excel/app";
import ExcelJS from "exceljs";
import TutorialsVideo from "../../components/TutorialsVideo";

dayjs.locale("vn");
const { RangePicker } = DatePicker;
type RangePickerProps = GetProps<typeof DatePicker.RangePicker>;

type FieldType = {
  booking_date: string;
  range_date_report: string;
  office_id: number | string;
};

const ReportTotalAgent = () => {
  const [form] = Form.useForm();
  const dispatch = useAppDispatch();
  const [page, setPage] = useState<number>(1);
  const [timeReport, setTimeReport] = useState<RangePickerProps["value"]>();
  const [optionsAgent, setOptionsAgent] = useState<ValueOptions[]>([]);

  const pagination = useAppSelector(
    (state) => state?.reportTotalAgent?.pagination
  );
  const listReport = useAppSelector(
    (state) => state?.reportTotalAgent?.listReport
  );
  const loadingListReport = useAppSelector(
    (state) => state?.reportTotalAgent?.loading
  );
  const totalCommission = useAppSelector(
    (state) => state?.reportTotalAgent?.total_commissions
  );
  const listFilter = useAppSelector((state) => state?.reportTotalAgent?.filter);
  const totalPricesPaidAtCompany = useAppSelector(
    (state) => state?.reportTotalAgent?.total_prices_paid_at_company
  );
  const totalPricesAgentBalance = useAppSelector(
    (state) => state?.reportTotalAgent?.total_prices_agent_balance
  );
  const listTutorials = useAppSelector(
    (state) => state?.tutorials?.listTutorials
  );

  const limit_pagination: any = window.localStorage.getItem("limit_pagination");
  const limitPagination = isNaN(Number(limit_pagination))
    ? Constant.PAGE_SIZE
    : Number(limit_pagination);

  const watchBookingDate = Form.useWatch("booking_date", form);
  const watchOffice = Form.useWatch("office_id", form);

  const handleGetListAgent = async () => {
    const response = await getListAgentAPI();
    if (response) {
      let listAgent: ValueOptions[] = response?.data?.data?.map(
        (agent: OfficeData) => ({
          value: agent?.id,
          label: agent?.name,
        })
      );
      listAgent = [{ value: "", label: "Tất cả" }, ...listAgent];
      setOptionsAgent(listAgent);
    }
  };

  useEffect(() => {
    handleGetListAgent();
  }, []);

  const onChangePage = async (page: number, pageSize: number) => {
    if (pagination?.per_page !== pageSize) {
      setPage(Constant.DEFAULT_PAGE);
      dispatch(setPerPage(pageSize));
    } else {
      setPage(page);
    }
    await dispatch(
      getListReportTotalAgent({
        booking_date: watchBookingDate,
        from: dayjs(timeReport?.[0]).format("YYYY-MM-DD"),
        to: dayjs(timeReport?.[1]).format("YYYY-MM-DD"),
        office_id: watchOffice?.label ? watchOffice?.value : watchOffice,
        page: pagination?.per_page !== pageSize ? Constant.DEFAULT_PAGE : page,
        limit: pageSize,
      })
    );
  };

  const onFinish: FormProps<any>["onFinish"] = async (values) => {
    setPage(Constant.DEFAULT_PAGE);
    const response = await dispatch(
      getListReportTotalAgent({
        booking_date: watchBookingDate,
        from: dayjs(timeReport?.[0]).format("YYYY-MM-DD"),
        to: dayjs(timeReport?.[1]).format("YYYY-MM-DD"),
        office_id: watchOffice?.label ? watchOffice?.value : watchOffice,
        page: Constant.DEFAULT_PAGE,
        limit: pagination?.per_page || limitPagination,
      })
    );
    if (response?.payload?.success) {
      toast.success(response?.payload?.message);
    }
  };

  const handleGetListReportBegin = async () => {
    setPage(Constant.DEFAULT_PAGE);
    if (listFilter?.from && listFilter?.to) {
      await dispatch(
        getListReportTotalAgent({
          booking_date: listFilter?.booking_date,
          from: dayjs(listFilter?.from).format("YYYY-MM-DD"),
          to: dayjs(listFilter?.to).format("YYYY-MM-DD"),
          office_id: listFilter?.office_id,
          page: Constant.DEFAULT_PAGE,
          limit: pagination?.per_page || limitPagination,
        })
      );
    }
  };

  useEffect(() => {
    handleGetListReportBegin();
    if (listFilter?.from && listFilter?.to) {
      setTimeReport([
        dayjs(listFilter?.from, "YYYY-MM-DD"),
        dayjs(listFilter?.to, "YYYY-MM-DD"),
      ]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns: TableColumnsType<DataReport> = [
    {
      title: "Tên đại lý",
      dataIndex: "agent",
      render: (agent) => (
        <Typography.Text className="fs-16 text-color fw-500">
          {agent?.name ? agent?.name : ""}
        </Typography.Text>
      ),
      sorter: (a: any, b: any) => a?.agent?.name.localeCompare(b?.agent?.name),
    },
    {
      title: "% Chiết khấu",
      dataIndex: "commission_percent",
      align: "right",
      render: (commission_percent) => (
        <Typography.Text className="fs-16 text-color fw-500">
          {`${commission_percent ? commission_percent : "0%"}${
            commission_percent !== 0 ? "%" : ""
          }`}
        </Typography.Text>
      ),
    },
    {
      title: "SL vé đã bán",
      align: "right",
      dataIndex: "total_paid_booking",
      render: (total_paid_booking) => (
        <Typography.Text className="fs-16 text-color fw-500">
          {total_paid_booking ? formatPrice(total_paid_booking, 0, ",") : 0}
        </Typography.Text>
      ),
    },
    {
      title: "SL vé đã hủy",
      align: "right",
      dataIndex: "total_cancel_booking",
      render: (total_cancel_booking) => (
        <Typography.Text className="fs-16 text-color fw-500">
          {total_cancel_booking ? formatPrice(total_cancel_booking, 0, ",") : 0}
        </Typography.Text>
      ),
    },
    {
      title: "Nhà xe thu hộ",
      align: "right",
      dataIndex: "total_price_paid_at_company",
      render: (total_price_paid_at_company) => (
        <Typography.Text className="fs-16 text-color fw-500">
          {total_price_paid_at_company
            ? formatPrice(total_price_paid_at_company, 0, ",")
            : 0}
        </Typography.Text>
      ),
    },
    {
      title: "Tài khoản đại lý",
      align: "right",
      dataIndex: "total_price_agent_balance",
      render: (total_price_agent_balance) => (
        <Typography.Text className="fs-16 text-color fw-500">
          {total_price_agent_balance
            ? formatPrice(total_price_agent_balance, 0, ",")
            : 0}
        </Typography.Text>
      ),
    },
    {
      title: "Tiền chiết khấu",
      dataIndex: "total_commision",
      align: "right",
      render: (total_commision) => (
        <Typography.Text className="fs-16 text-color fw-500">
          {total_commision ? formatPrice(total_commision, 0, ",") : 0}
        </Typography.Text>
      ),
    },
  ];

  const handleGetLabelAgent = (value: number | string) => {
    const agentCurrent = optionsAgent?.find((agent) => agent?.value === value);
    return agentCurrent?.label || "Tất cả";
  };

  const handleGetLabelTypeDateRoute = (value: string) => {
    const typeCurrent = optionsBookingDate?.find(
      (type) => type?.value === value
    );
    return typeCurrent?.label;
  };

  const onChangeTimeReport = (value: RangePickerProps["value"]) => {
    setTimeReport(value);
    dispatch(setFilterRangeDate(value));
  };

  const onChangeAgent = (value: number | undefined) => {
    dispatch(setFilterAgent(value));
  };

  const onChangeTypeDateReport = (type: string) => {
    dispatch(setFilterBookingDate(type));
  };

  const columnsExport: IExcelColumn[] = [
    {
      title: "Tên đại lý",
      dataIndex: "agent",
      __excelTitle__: "Tên đại lý",
      width: 40,
      __style__: {
        fontName: "Times New Roman",
        fontSize: 14,
        border: true,
        borderColor: "black",
      },
    },
    {
      title: "% Chiết khấu",
      dataIndex: "commission_percent",
      __excelTitle__: "% Chiết khấu",
      __numFmt__: "0.0%",
      width: 20,
      __style__: {
        fontName: "Times New Roman",
        fontSize: 14,
        border: true,
        borderColor: "black",
        h: "right",
      },
    },
    {
      title: "SL vé đã bán",
      dataIndex: "total_paid_booking",
      __cellType__: "TypeNumeric",
      __excelTitle__: "SL vé đã bán",
      __numFmt__: "#,##0",
      width: 20,
      __style__: {
        fontName: "Times New Roman",
        fontSize: 14,
        border: true,
        borderColor: "black",
        h: "right",
      },
    },
    {
      title: "SL vé đã hủy",
      dataIndex: "total_cancel_booking",
      __cellType__: "TypeNumeric",
      __excelTitle__: "SL vé đã hủy",
      __numFmt__: "#,##0",
      width: 20,
      __style__: {
        fontName: "Times New Roman",
        fontSize: 14,
        border: true,
        borderColor: "black",
        h: "right",
      },
    },
    {
      title: "Nhà xe thu hộ",
      dataIndex: "total_price_paid_at_company",
      __cellType__: "TypeNumeric",
      __excelTitle__: "Nhà xe thu hộ",
      __numFmt__: "#,##0",
      width: 20,
      __style__: {
        fontName: "Times New Roman",
        fontSize: 14,
        border: true,
        borderColor: "black",
        h: "right",
      },
    },
    {
      title: "Tài khoản đại lý",
      dataIndex: "total_price_agent_balance",
      __cellType__: "TypeNumeric",
      __excelTitle__: "Tài khoản đại lý",
      __numFmt__: "#,##0",
      width: 20,
      __style__: {
        fontName: "Times New Roman",
        fontSize: 14,
        border: true,
        borderColor: "black",
        h: "right",
      },
    },
    {
      title: "Tiền chiết khấu",
      dataIndex: "total_commision",
      __cellType__: "TypeNumeric",
      __excelTitle__: "Tiền chiết khấu",
      __numFmt__: "#,##0",
      width: 20,
      __style__: {
        fontName: "Times New Roman",
        fontSize: 14,
        border: true,
        borderColor: "black",
        h: "right",
      },
    },
    {
      title: "Tiền chiết khấu nhà xe trả",
      dataIndex: "commision_paid_at_company",
      __cellType__: "TypeNumeric",
      __excelTitle__: "Tiền chiết khấu nhà xe trả",
      __numFmt__: "#,##0",
      width: 40,
      __style__: {
        fontName: "Times New Roman",
        fontSize: 14,
        border: true,
        borderColor: "black",
        h: "right",
      },
    },
  ];

  const handleExportExcel = async () => {
    const response: any = await dispatch(
      getListReportTotalAgentExcel({
        booking_date: watchBookingDate,
        from: dayjs(timeReport?.[0]).format("YYYY-MM-DD"),
        to: dayjs(timeReport?.[1]).format("YYYY-MM-DD"),
        office_id: watchOffice?.label ? watchOffice?.value : watchOffice,
      })
    );
    if (!response?.error) {
      const listData = response?.payload?.data;
      const totalCommissions = response?.payload?.total_commissions;
      const totalPricesAgentBalance =
        response?.payload?.total_prices_agent_balance;
      const totalPricesPaidAtCompany =
        response?.payload?.total_prices_paid_at_company;
      const totalCommissionsCompany =
        response?.payload?.total_commissions_company;
      const listDataExcel =
        listData?.length > 0
          ? listData?.map((report: DataReport) => ({
              agent: report?.agent?.name ? report?.agent?.name : "",
              commission_percent: Number(report?.commission_percent) / 100,
              total_paid_booking: report?.total_paid_booking
                ? Number(report?.total_paid_booking)
                : 0,
              total_cancel_booking: report?.total_cancel_booking
                ? Number(report?.total_cancel_booking)
                : 0,
              total_price_paid_at_company: report?.total_price_paid_at_company
                ? Number(report?.total_price_paid_at_company)
                : 0,
              total_price_agent_balance: report?.total_price_agent_balance
                ? Number(report?.total_price_agent_balance)
                : 0,
              total_commision: report?.total_commision
                ? Number(report?.total_commision)
                : 0,
              total_price: report?.total_price
                ? Number(report?.total_price)
                : 0,
              commision_paid_at_company: report?.commision_paid_at_company
                ? Number(report?.commision_paid_at_company)
                : 0,
            }))
          : [];

      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Báo cáo tổng hợp theo đại lý");
      const header = columnsExport.map((col) => ({
        header: col.title,
        key: col.dataIndex as string,
      }));
      worksheet.columns = header;
      columnsExport.forEach((col, index) => {
        worksheet.getColumn(index + 1).width = col.width || 15;
        worksheet.getColumn(index + 1).alignment = {
          horizontal:
            (col?.__style__?.h as
              | "center"
              | "left"
              | "right"
              | "fill"
              | "justify") || "left",
          vertical: "middle",
        };
        worksheet.getColumn(index + 1).numFmt = col.__numFmt__ || "general";
      });
      worksheet.addRows(listDataExcel);
      worksheet.getRow(1).font = {
        name: "Times New Roman",
        size: 12,
        bold: true,
      };
      worksheet.getRow(1).eachCell((cell, colNumber) => {
        if (colNumber < 9) {
          cell.fill = {
            type: "pattern",
            pattern: "solid",
            fgColor: { argb: "3AB54A" },
          };
          cell.border = {
            top: { style: "thin" },
            left: { style: "thin" },
            bottom: { style: "thin" },
            right: { style: "thin" },
          };
        }
      });
      let firstRowHasData = false;
      worksheet.getRow(1).eachCell((cell, colNumber) => {
        if (cell.value) {
          firstRowHasData = true;
          return;
        }
      });
      if (firstRowHasData) {
        worksheet.eachRow({ includeEmpty: true }, (row, rowNumber) => {
          if (rowNumber > 1) {
            row.eachCell((cell) => {
              cell.font = { name: "Times New Roman", size: 12 };
              cell.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
              };
            });
          }
        });
      } else {
        worksheet.eachRow((row, rowNumber) => {
          if (rowNumber > 1) {
            let hasData = false;
            row.eachCell((cell) => {
              if (cell.value) {
                hasData = true;
                return;
              }
            });
            if (hasData) {
              row.border = {
                top: { style: "thin" },
                left: { style: "thin" },
                bottom: { style: "thin" },
                right: { style: "thin" },
              };
            }
          }
        });
      }
      const startColumnIndex = 1;

      const subLastRow = worksheet.addRow([]);
      subLastRow.getCell(1).value = "TỔNG (VND)";
      subLastRow.getCell(1).alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      subLastRow.getCell(1).font = {
        name: "Times New Roman",
        size: 14,
        bold: true,
      };
      const endColumnIndexTotal = startColumnIndex + columnsExport.length - 4;
      worksheet.mergeCells(
        subLastRow.number,
        1,
        subLastRow.number,
        endColumnIndexTotal
      );
      subLastRow.getCell(5).value = totalPricesPaidAtCompany;
      subLastRow.getCell(6).value = totalPricesAgentBalance;
      subLastRow.getCell(7).value = totalCommissions;
      subLastRow.getCell(8).value = totalCommissionsCompany;

      subLastRow.eachCell((cell) => {
        cell.font = { name: "Times New Roman", size: 14, bold: true };
        cell.border = {
          top: { style: "thin" },
          left: { style: "thin" },
          bottom: { style: "thin" },
          right: { style: "thin" },
        };
      });

      const lastRow = worksheet.addRow([]);
      lastRow.getCell(1).value = "@Bản quyền thuộc nhà xe Carlink";
      lastRow.getCell(1).alignment = {
        vertical: "middle",
        horizontal: "left",
      };
      lastRow.getCell(1).font = {
        name: "Times New Roman",
        size: 14,
        bold: true,
      };

      const endColumnIndex = startColumnIndex + columnsExport.length - 1;
      worksheet.mergeCells(lastRow.number, 1, lastRow.number, endColumnIndex);

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], {
        type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Báo cáo tổng hợp theo đại lý.xlsx";
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
    }
  };

  const filterOption = (
    input: string,
    option?: {
      label?: any;
      value?: any;
    }
  ) => (option?.label ?? "").toLowerCase().includes(input.toLowerCase());

  const systemConfig = useAppSelector((state) => state.systemConfig?.result);
  const disabledDaysDate = useCallback(
    (current: any, { from }: any) => {
      if (from) {
        return (
          Math.abs(current.diff(from, "days")) >=
          systemConfig?.export_report_month
        );
      }
      return false;
    },
    [systemConfig?.export_report_month]
  );

  return (
    <div className="Report bg-white">
      <Form
        form={form}
        layout="vertical"
        onFinish={onFinish}
        initialValues={{
          booking_date: listFilter?.booking_date,
          range_date_report: [
            listFilter?.from
              ? dayjs(listFilter?.from, "DD/MM/YYYY")
              : undefined,
            listFilter?.to ? dayjs(listFilter?.to, "DD/MM/YYYY") : undefined,
          ],
          office_id: listFilter?.office_id
            ? listFilter?.office_id
            : { value: "", label: "Chọn đại lý" },
        }}
      >
        <Row gutter={12}>
          <Col className="Report__typeDateReport">
            <Form.Item<FieldType>
              className="required"
              name="booking_date"
              label="Khoảng thời gian"
              rules={[
                { required: true, message: "Vui lòng chọn khoảng thời gian" },
              ]}
            >
              <Select
                placeholder="Theo ngày khởi hành"
                options={optionsBookingDate}
                onChange={onChangeTypeDateReport}
              />
            </Form.Item>
          </Col>
          <Col className="Report__rangeDateReport">
            <Form.Item<FieldType>
              name="range_date_report"
              rules={[
                { required: true, message: "Vui lòng chọn khoảng thời gian" },
              ]}
            >
              <RangePicker
                disabledDate={disabledDaysDate}
                format="DD/MM/YYYY"
                onChange={onChangeTimeReport}
                popupClassName="Report__timeReport__picker"
                placeholder={["Từ ngày", "Đến ngày"]}
              />
            </Form.Item>
          </Col>
        </Row>
        <Flex align="center" gap={"small"} className="mb-24">
          <Image
            preview={false}
            width={20}
            height={20}
            className="d-flex"
            src={IconExclamationCircle}
            alt="Icon Exclamation Circle"
          />
          <Typography.Text className="fs-14 fw-500 text-color">
            Vui lòng chọn khoảng thời gian để xem báo cáo
          </Typography.Text>
        </Flex>
        <Typography.Text className="fs-6 fw-500 text-color d-block mb-8">
          Bộ lọc nâng cao
        </Typography.Text>
        <Flex gap={"small"}>
          <Form.Item<FieldType> className="Report__agent" name="office_id">
            <Select
              onChange={onChangeAgent}
              options={optionsAgent}
              showSearch
              filterOption={filterOption}
            />
          </Form.Item>
        </Flex>
        <Flex align="center" justify="space-between">
          <Form.Item className="Report__submit">
            <ConfigProvider theme={{ token: { colorPrimary: "#435869" } }}>
              <Button
                disabled={timeReport?.length === 2 ? false : true}
                className="d-flex align-items-center h-auto"
                type="primary"
                htmlType="submit"
              >
                <IconFileExport width={16} height={16} />
                Xem báo cáo
              </Button>
            </ConfigProvider>
          </Form.Item>
          <TutorialsVideo url_youtube={handleGetUrlGuide(listTutorials, 2)} />
        </Flex>
        <Divider />
        <Flex>
          <ConfigProvider theme={{ token: { colorPrimary: "#435869" } }}>
            <Button
              ghost
              disabled={
                timeReport?.length === 2 || (listFilter?.from && listFilter?.to)
                  ? false
                  : true
              }
              onClick={handleExportExcel}
              className={`Report__exportExcel d-flex align-items-center mr-12 h-auto ${
                timeReport?.length === 2 || (listFilter?.from && listFilter?.to)
                  ? ""
                  : "disabled"
              }`}
              type="primary"
            >
              <IconExportExcel
                color={`${
                  timeReport?.length === 2 ||
                  (listFilter?.from && listFilter?.to)
                    ? "#435869"
                    : "#949494"
                }`}
                width={16}
                height={16}
              />
              Xuất Excel
            </Button>
          </ConfigProvider>
        </Flex>
        <Typography.Text className="d-block text-color-gray-3A3A3A fs-18 fw-600 mt-24 mb-8">
          Báo cáo tổng hợp theo đại lý (
          {handleGetLabelTypeDateRoute(watchBookingDate)})
        </Typography.Text>
        <Flex wrap="wrap" align="center" className="Report__filter">
          {timeReport && (
            <Flex align="center" className="Report__filter__item">
              <Typography.Text className="d-block fs-16 fw-500">
                Từ ngày:
              </Typography.Text>
              <Typography.Text className="d-block fs-16 fw-600">
                {dayjs(timeReport?.[0]).format("DD/MM/YYYY")}
              </Typography.Text>
            </Flex>
          )}
          {timeReport && (
            <Flex align="center" className="Report__filter__item">
              <Typography.Text className="d-block fs-16 fw-500">
                Đến ngày:
              </Typography.Text>
              <Typography.Text className="d-block fs-16 fw-600">
                {dayjs(timeReport?.[1]).format("DD/MM/YYYY")}
              </Typography.Text>
            </Flex>
          )}
          <Flex align="center" className="Report__filter__item">
            <Typography.Text className="d-block fs-16 fw-500">
              Đại lý:
            </Typography.Text>
            <Typography.Text className="d-block fs-16 fw-600">
              {handleGetLabelAgent(watchOffice)}
            </Typography.Text>
          </Flex>
        </Flex>
      </Form>
      <Table
        className="Report__table"
        locale={{
          triggerDesc: "Sắp xếp tăng dần",
          triggerAsc: "Hủy sắp xếp",
          cancelSort: "Sắp xếp giảm dần",
        }}
        pagination={{
          total: pagination?.total,
          current: page,
          pageSize: pagination?.per_page ?? DEFAULT_PAGE_SIZE,
          position: ["bottomRight"],
          onChange: onChangePage,
          showSizeChanger: true,
        }}
        dataSource={listReport?.length > 0 ? listReport : []}
        loading={loadingListReport}
        columns={columns}
        summary={(pageData) => {
          return (
            listReport?.length > 0 &&
            page === pagination?.last_page && (
              <>
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={4}>
                    <Flex align="center" gap="middle">
                      <Typography.Text className="text-color-default fw-700 fs-16">
                        TỔNG
                      </Typography.Text>
                      <Typography.Text className="text-color-default fw-500 fs-16">
                        (VND)
                      </Typography.Text>
                    </Flex>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right" index={4}>
                    <Typography.Text className="text-color-default text-end fw-700 fs-16">
                      {totalPricesPaidAtCompany
                        ? formatPrice(totalPricesPaidAtCompany, 0, ",")
                        : 0}
                    </Typography.Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right" index={5}>
                    <Typography.Text className="text-color-default text-end fw-700 fs-16">
                      {totalPricesAgentBalance
                        ? formatPrice(totalPricesAgentBalance, 0, ",")
                        : 0}
                    </Typography.Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell align="right" index={6}>
                    <Typography.Text className="text-color-default text-end fw-700 fs-16">
                      {totalCommission
                        ? formatPrice(totalCommission, 0, ",")
                        : 0}
                    </Typography.Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              </>
            )
          );
        }}
      />
    </div>
  );
};

export default ReportTotalAgent;
